import { createContext, useState, useMemo, useEffect } from "react";
import { createTheme } from "@mui/material/styles";
import * as coreLocales from "@mui/material/locale";
import * as gridLocales from "@mui/x-data-grid/locales";
import * as pickerLocales from "@mui/x-date-pickers/locales";
import { useTranslation } from "react-i18next";
import { tokens } from "./tokens";
import { palette } from "./palette";
import { typography } from "./typography";
import { components } from "./components";
import { breakpoints } from "./breakpoints";
import { shadows } from "./shadows";
import { zIndex } from "./zIndex";

// Export tokens for backward compatibility
export { tokens };

// MUI theme settings
export const themeSettings = (mode) => {
  return {
    palette: palette(mode),
    typography: typography(),
    shape: {
      borderRadius: 2,
    },
    transitions: {
      easing: {
        easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
        easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
        easeIn: "cubic-bezier(0.4, 0, 1, 1)",
        sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
      },
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
    zIndex: zIndex(),
    spacing: (factor) => `${0.25 * factor}rem`,
    breakpoints: breakpoints(),
    shadows: shadows(),
    components: components(mode),
  };
};

// i18n language → MUI locale pack name. Gives DataGrid, pagination and pickers
// their built-in translations ("Rækker pr. side" instead of "Rows per page").
const MUI_LOCALES = { da: "daDK", nb: "nbNO", sv: "svSE", de: "deDE", en: "enUS" };

// "1–25 of 80": missing from MUI's Nordic/German packs, so it stayed English
const OF_WORD = { da: "af", nb: "av", sv: "av", de: "von", en: "of" };

const muiLocalesFor = (language) => {
  const lang = (language || "en").split("-")[0];
  const name = MUI_LOCALES[lang] || "enUS";
  const of = OF_WORD[lang] || "of";
  const displayedRows = ({ from, to, count }) =>
    `${from}–${to} ${of} ${count !== -1 ? count : `> ${to}`}`;
  const paginationFix = {
    components: {
      MuiTablePagination: { defaultProps: { labelDisplayedRows: displayedRows } },
      MuiDataGrid: {
        defaultProps: {
          localeText: {
            paginationDisplayedRows: ({ from, to, count }) => displayedRows({ from, to, count }),
          },
        },
      },
    },
  };
  return [coreLocales[name], gridLocales[name], pickerLocales[name], paginationFix];
};

// Helper function to get initial theme mode from localStorage
const getInitialMode = () => {
  try {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode === 'dark' || savedMode === 'light' ? savedMode : 'light';
  } catch (error) {
    // In case localStorage is not available (e.g., SSR)
    return 'light';
  }
};

// Helper function to save theme mode to localStorage
const saveThemeMode = (mode) => {
  try {
    localStorage.setItem('themeMode', mode);
  } catch (error) {
    // Handle case where localStorage is not available
    console.warn('Could not save theme mode to localStorage:', error);
  }
};

// Context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => { }
});

export const useMode = () => {
  const [mode, setMode] = useState(getInitialMode);
  const { i18n } = useTranslation();

  // Save to localStorage whenever mode changes
  useEffect(() => {
    saveThemeMode(mode);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(
    () => createTheme(themeSettings(mode), ...muiLocalesFor(i18n.language)),
    [mode, i18n.language]
  );

  return [theme, colorMode];
};